import { Tooltip } from "antd";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import DataTable from "../../UI/DataTable/DataTable";
import { Celda, CeldaDoble, ChipEstado, tituloDeNombre } from "../../UI/DataTable/piezas";
import { accionDeFila } from "../../../utils/auditoriaBandeja";
import { chipElegibilidad, puedeCorregirse } from "../../../utils/auditoriaElegibilidad";
import { IconoLapiz } from "./AuditIconos";

// AUDITORIA-CLINICA-REDISENO -- la bandeja pasa a consumir el DataTable
// genérico (FASE 5, paso 4). Lo que antes era una tabla de antd con columnas a
// medida ahora sólo DECLARA columnas; la anatomía (cabecera de 40px, fila de
// 58px, truncado con `title`, fila activa, pie con el rango) vive en el
// componente y la comparten todas las pantallas que se migren después.
//
// Aplicación literal de las reglas de columnas del rediseño:
//
//  · Se fusiona lo que se lee junto: cita+fecha, paciente+documento,
//    aseguradora+plan. Tres columnas menos, cero datos perdidos.
//  · "Estado de la auditoría" NO es una columna: la pestaña activa ya fija ese
//    valor para todas las filas, así que repetirlo en cada fila no informa de
//    nada. Lo que sí queda es la ACCIÓN, que ya distingue Auditar / Continuar /
//    Ver. Si algún día la pestaña deja de filtrar, vuelve como columna.
//  · "Clínica" tampoco: el alcance del usuario ya la determina.
//  · El contador de correcciones en cero no se pinta: sólo aparece la pastilla
//    cuando hay correcciones de verdad.
//  · Una sola columna de acción, sin cabecera, a la derecha. La fila entera es
//    clicable.
const COBERTURA_CHIP = {
    SEGURO: { tono: "info", label: "Seguro" },
    PARTICULAR: { tono: "exito", label: "Particular" },
    SIN_DETERMINAR: { tono: "alerta", label: "Sin determinar" },
};

const fmtFecha = (t) => (t ? format(new Date(t), "dd/MM/yyyy", { locale: es }) : "—");
const fmtHora = (t) => (t ? format(new Date(t), "HH:mm", { locale: es }) : "—");

function BandejaTabla({
    rows,
    loading,
    error,
    page,
    pageSize,
    total,
    activa,
    onCambioPagina,
    onAbrir,
    onReintentar,
    hayFiltros,
    onLimpiarFiltros,
}) {
    const columnas = [
        {
            key: "idcita",
            titulo: "Cita",
            ancho: 130,
            render: (f) => (
                <CeldaDoble principal={`#${f.idcita}`} secundario={`${fmtFecha(f.fecha)} · ${fmtHora(f.fecha)}`} monoPrincipal />
            ),
        },
        {
            key: "paciente",
            titulo: "Paciente",
            render: (f) => (
                <CeldaDoble principal={tituloDeNombre(f.paciente) || "Sin determinar"} secundario={f.documento} monoSecundario />
            ),
        },
        { key: "doctor", titulo: "Médico", ancho: 190, render: (f) => <Celda valor={f.doctor || "Sin determinar"} /> },
        {
            key: "tipo_cobertura",
            titulo: "Cobertura",
            ancho: 150,
            render: (f) => {
                const chip = COBERTURA_CHIP[f.tipo_cobertura] || { tono: "neutro", label: f.tipo_cobertura || "—" };
                return <ChipEstado tono={chip.tono}>{chip.label}</ChipEstado>;
            },
        },
        {
            key: "aseguradora",
            titulo: "Aseguradora / plan",
            ancho: 210,
            render: (f) => <CeldaDoble principal={f.aseguradora} secundario={f.plan || "Sin plan"} />,
        },
        {
            key: "accion",
            titulo: "",
            ancho: 250,
            alinear: "derecha",
            render: (f) => {
                const corregible = puedeCorregirse(f);
                const accion = corregible ? accionDeFila(f.estado_auditoria) : { label: "Ver", tipo: "default" };
                const chip = chipElegibilidad(f);
                return (
                    <div className="ac-celda-accion">
                        {f.cantidad_correcciones > 0 && (
                            <Tooltip title={`${f.cantidad_correcciones} corrección(es) registradas`}>
                                <span className="dt-chip dt-chip--info" data-testid={`ac-bandeja-correcciones-${f.idcita}`}>
                                    <IconoLapiz size={12} />
                                    {f.cantidad_correcciones}
                                </span>
                            </Tooltip>
                        )}
                        {chip && (
                            <Tooltip title={chip.detalle}>
                                <span
                                    className={`dt-chip dt-chip--${chip.color === "error" ? "peligro" : "exito"}`}
                                    data-testid={`ac-bandeja-elegibilidad-${f.idcita}`}
                                >
                                    <span className="dt-chip__punto" aria-hidden="true" />
                                    {chip.label}
                                </span>
                            </Tooltip>
                        )}
                        <button
                            type="button"
                            className={`dt__btn${accion.tipo === "primary" ? " dt__btn--primario" : ""}`}
                            onClick={(e) => {
                                e.stopPropagation();
                                onAbrir(f);
                            }}
                            data-testid={`ac-bandeja-accion-${f.idcita}`}
                        >
                            {accion.label}
                        </button>
                    </div>
                );
            },
        },
    ];

    return (
        <DataTable
            columnas={columnas}
            filas={rows}
            filaKey="idcita"
            cargando={loading}
            error={error}
            onReintentar={onReintentar}
            filaActiva={activa}
            onFilaClick={onAbrir}
            etiquetaTabla="Atenciones para auditar"
            hayFiltrosAplicados={hayFiltros}
            onLimpiarFiltros={onLimpiarFiltros}
            textoVacioSinFiltros="No hay atenciones finalizadas en este estado."
            paginacion={{ page, pageSize, total, onCambio: onCambioPagina }}
        />
    );
}

export default BandejaTabla;
